import { useRef, useState } from 'react';
import {
  Button,
  Card,
  Field,
  I,
  Input,
  PageHeader,
  Stack,
  Textarea,
  useDelayedLoading,
} from '@leanin/ui';
import { SaveBar } from '../../form/SaveBar';
import { useSpace } from '../../../lib/queries/spaces';
import { useUpdateSpace } from '../../../lib/mutations/spaces';
import {
  AdminImagePanel,
  type AdminImagePanelHandle,
} from '../../group/admin/AdminImagePanel';
import { AdminFormPageSkeleton } from '../../group/admin/AdminFormPageSkeleton';

type Props = {
  readonly id: string;
};

// Overview is identity + branding only. The access/joinPolicy axes live on
// the Visibility tab, shared with circles/networks via GroupVisibilitySection.
export function SpaceOverviewSection({ id }: Props) {
  const detailQuery = useSpace(id);
  const space = detailQuery.data ?? null;

  const showSkeleton = useDelayedLoading(space === null);
  if (showSkeleton) {
    return <AdminFormPageSkeleton withAction cards={1} rowsPerCard={3} />;
  }
  if (space === null) return null;

  return <SpaceOverviewForm space={space} />;
}

type FormProps = {
  readonly space: NonNullable<ReturnType<typeof useSpace>['data']>;
};

function SpaceOverviewForm({ space }: FormProps) {
  const updateMutation = useUpdateSpace();
  const [name, setName] = useState(space.name);
  const [description, setDescription] = useState(space.description);
  const [memberRules, setMemberRules] = useState(space.memberRules ?? '');
  const [error, setError] = useState<string | null>(null);

  const imagesRef = useRef<AdminImagePanelHandle | null>(null);
  const [imagesPending, setImagesPending] = useState(false);
  const [committing, setCommitting] = useState(false);
  const isPending = updateMutation.isPending || committing;

  const textDirty =
    name.trim() !== space.name.trim() ||
    description !== space.description ||
    memberRules !== (space.memberRules ?? '');
  const dirty = textDirty || imagesPending;

  const handleSave = async () => {
    if (!dirty) return;
    setError(null);
    if (name.trim().length === 0) {
      setError('Name is required.');
      return;
    }
    if (imagesPending) {
      setCommitting(true);
      try {
        await imagesRef.current?.commit();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Image upload failed.');
        setCommitting(false);
        return;
      }
      setCommitting(false);
    }
    if (!textDirty) return;
    updateMutation.mutate(
      {
        id: space.id,
        name: name.trim(),
        description,
        memberRules: memberRules.trim() || null,
      },
      {
        onError: (err) =>
          setError(err instanceof Error ? err.message : 'Failed to update Group.'),
      },
    );
  };

  return (
    <Stack gap="lg">
      <PageHeader title="Group overview" />

      <Card pad="flush">
        <AdminImagePanel
          ref={imagesRef}
          groupKind="space"
          id={space.id}
          groupName={space.name}
          currentCoverUrl={space.imageUrl}
          currentLogoUrl={space.logoUrl}
          onPendingChange={setImagesPending}
        />

        <Stack p="xl" gap="lg">
          <Field label="Name" required>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={80}
            />
          </Field>

          <Field label="Description">
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              maxLength={2000}
            />
          </Field>

          <Field
            label="House rules"
            description="Optional ground rules shown to members on the Group page."
          >
            <Textarea
              value={memberRules}
              onChange={(e) => setMemberRules(e.target.value)}
              rows={4}
              maxLength={4000}
              placeholder="e.g. Be kind, no self-promotion, stay on topic."
            />
          </Field>
        </Stack>
      </Card>

      <SaveBar
        dirty={dirty}
        saving={isPending}
        error={error}
        onSave={handleSave}
      />
    </Stack>
  );
}
